import { compareAsc, compareDesc, getYear, isPast, startOfTomorrow, } from 'date-fns';
import * as yup from 'yup';
import { CivilStatusEnum, CountryIsoCodeEnum, } from '@zurich/api/party';
import { diffInYearsAsNumber, parseISODate } from '@zurich/common/forms';
import { isCommercialRegisterNumber, isDigit, isDoorNr, isEmail, isIBAN, isMessage, isNoSpecialCharacters, isProviderCode, isStairwell, isStreetName, isTelNr, isVAT, } from '@zurich-client/validator/utils';
import { partyDocumentDocSubTypeUtils, partymodificationReasoneUtils, partyRelationVoucherTypeUtils, } from '../utils';
import { FixedParty, PartyCommunicationRole, PartyCountryCallingCode, PartyCountryIsoCode, PartyEducation, PartyEmployment, PartyLegalForm, PartyOccupation, PartyOrganizationType, PartySalutation, PartySubType, } from '../utils/notMigratedYet';
export function partyIsNoSpecialCharacters(value) {
    return /^[\u00C0-\u0192a-zA-Z0-9\s\-§!%]*$/.test(value || '');
}
export const numberSchema = yup
    .string()
    .nullable()
    .test('isDigit', '1', isDigit);
export function partyIsNameCharacters(value) {
    return /^([\u00C0-\u0192a-zA-Z\s-]*)$/.test(value || '');
}
export function partyCheckBirthday(dateOfBirthValue) {
    if (dateOfBirthValue) {
        const dateOfBirth = new Date(dateOfBirthValue);
        const dateOld = new Date('1.1.1960');
        return compareAsc(dateOld, dateOfBirth) > 0;
    }
    return false;
}
export function partyHasTwoLetters(value) {
    return /[\u00C0-\u0192a-zA-Z]{2,}/.test(value || '');
}
export function partyTitleLengthIsMax40(value, otherValue) {
    const humanReadableValues = [...value, ...otherValue];
    return humanReadableValues.join('').replace(/\s+/g, '').length <= 40;
}
export const partySubTypeSchema = yup
    .string()
    .oneOf(PartySubType.getAllValues(), 'required')
    .nullable()
    .required('required');
export const partySalutationSchema = yup
    .string()
    .oneOf(PartySalutation.getAllValues(), 'required')
    .nullable()
    .required('required');
export const partyAcademicAffixSchema = yup.object({
    partyAcademicDegreePrefix: yup
        .array()
        .of(yup.string())
        .nullable()
        .test('partyAcademicDegreePrefix', '1', (value, context) => {
        const { partyAcademicDegreePostfix } = context.parent;
        if (value && Array.isArray(value)) {
            const filtered = value.filter((v) => v !== undefined);
            return partyTitleLengthIsMax40(filtered, partyAcademicDegreePostfix);
        }
        return true;
    }),
    partyAcademicDegreePostfix: yup
        .array()
        .of(yup.string())
        .nullable()
        .test('partyAcademicDegreePostfix', '1', (value, context) => {
        const { partyAcademicDegreePrefix } = context.parent;
        if (value && Array.isArray(value)) {
            const filtered = value.filter((v) => v !== undefined);
            return partyTitleLengthIsMax40(filtered, partyAcademicDegreePrefix);
        }
        return true;
    }),
});
// field disabled for now
//export const partyDegreeAwardedSchema = yup.array().of(yup.string()).nullable();
export const partyNameSchema = yup
    .string()
    .max(50)
    .nullable()
    .required('required')
    .test('partyIsNoSpecialCharacterAndLength', 'required', (value) => {
    if (value) {
        return partyHasTwoLetters(value);
    }
    return false;
});
export const partyNameLiveSchema = yup
    .string()
    .test('isNameCharacters', 'none', partyIsNameCharacters);
export const partyNameAffixSchema = yup
    .string()
    .max(75)
    .nullable()
    .test('partyIsNoSpecialCharacterAndLength', 'required', (value) => {
    if (value) {
        return partyHasTwoLetters(value);
    }
    return true;
});
export const partyOrganizationNameSchema = yup
    .string()
    .min(2, 'required')
    .nullable()
    .required('required');
export const partyBusinessNameSchema = yup
    .string()
    .min(2, 'required')
    .nullable();
export const partyDateOfBirthSchemaNonRequired = yup
    .date()
    .transform(parseISODate)
    .test('< tomorrow', '1', (value) => (value ? isPast(value) : true))
    .nullable();
export const partyDateOfBirthSchema = yup
    .date()
    .typeError('required')
    .transform(parseISODate)
    .test('< tomorrow', '1', (value) => (value ? isPast(value) : true))
    .required('required');
export const partyDateOfBirthUnderAgeSchema = partyDateOfBirthSchema.test('customer is older than 17 years', 'min17YearsOld', (value) => (value ? diffInYearsAsNumber(new Date(), value) >= 17 : true));
export const partyDateOfDeathSchema = yup.object({
    dateOfDeath: yup
        .date()
        .nullable()
        .transform(parseISODate)
        .test('before dateOfBirth', '1', (dateOfDeath, context) => {
        if (context.parent.dateOfBirth && dateOfDeath) {
            const dateOfBirth = new Date(context.parent.dateOfBirth);
            const today = new Date();
            // cannot be in the future and needs to be after dateOfBirth
            return (compareAsc(dateOfDeath, today) < 0 &&
                compareAsc(dateOfBirth, dateOfDeath) < 0);
        }
        return true;
    }),
});
export const partyCitizenshipsSchema = yup
    .array()
    .of(yup.string())
    .min(1, 'required')
    .required('required');
export const partyCitizenshipsNonRequiredSchema = yup
    .array()
    .of(yup.string())
    .nullable();
export const partyOccupationSchema = yup
    .string()
    .oneOf([null, ...PartyOccupation.getAllValues()], 'required')
    .nullable();
export const partyBusinessSectorsSchema = yup.mixed().nullable();
export const partyLegalFormNotRequiredSchema = yup
    .string()
    .nullable()
    .required('required');
export const partyLegalFormSchema = yup
    .string()
    .nullable()
    .oneOf(PartyLegalForm.getAllValues(), 'required')
    .required('required');
export const partyOwnershipCheckSchema = yup.boolean();
export const partyPubliclyTradedSchema = yup.boolean();
export const partyMajorCustomerSchema = yup.boolean();
export const partyClubObjectiveSchema = yup
    .string()
    .nullable()
    .test('isNoSpecialCharacters', '1', partyIsNoSpecialCharacters);
export const partyOrganizationTypeSchema = yup
    .string()
    .oneOf([null, ...PartyOrganizationType.getAllValues()], 'required')
    .nullable();
export const partyFoundingYearSchema = yup
    .string()
    .nullable()
    .max(4, '1')
    .test('isDigit', 'none', isDigit);
export const partyLiquidationDateSchema = yup.object({
    liquidationDate: yup
        .date()
        .transform(parseISODate)
        .nullable()
        .test('after foundingYear', '1', (liquidationDate, context) => {
        if (context.parent.foundingYear && liquidationDate) {
            const foundingYear = Number(context.parent.foundingYear);
            const liquidationYear = getYear(liquidationDate);
            return foundingYear <= liquidationYear;
        }
        return true;
    }),
});
export const partyPlaceOfBirthSchema = yup.string().nullable();
export const partyCountryOfBirthSchema = yup.string().nullable();
export const partyCivilStatusSchema = yup.object({
    civilStatus: yup
        .string()
        .nullable()
        .test('not ledig', '1', (civilStatus, context) => {
        if (civilStatus && context.parent.dateOfMarriage) {
            const today = new Date();
            const dateOfMarriage = new Date(context.parent.dateOfMarriage);
            // if marriage in the past party can't be "Ledig"
            if (compareAsc(dateOfMarriage, today) < 0) {
                return civilStatus !== CivilStatusEnum.LED;
            }
        }
        return true;
    }),
});
export const partyMarriageOnSchema = yup.object({
    dateOfMarriage: yup
        .date()
        .nullable()
        .transform(parseISODate)
        .test('before dateOfDeath', '1', (dateOfMarriage, context) => {
        if (context.parent.dateOfDeath && dateOfMarriage) {
            const dateOfDeath = new Date(context.parent.dateOfDeath);
            return compareAsc(dateOfMarriage, dateOfDeath) < 0;
        }
        return true;
    }),
});
export const partyCommercialRegisterNumberSchema = yup
    .string()
    .nullable()
    .test('isCommercialRegisterNumber', '1', isCommercialRegisterNumber);
export const partyUidSchema = yup.string().nullable().test('isVAT', '1', isVAT);
export const partyZvrNumberSchema = yup
    .string()
    .nullable()
    .test('validate zvr number', '1', (value) => value ? /^\d{1,11}$/.test(value || '') : true);
export const partyFixedPartySchema = yup
    .string()
    .oneOf([null, ...FixedParty.getAllValues()], 'required')
    .nullable();
export const partyVvoNumberSchema = yup.string().nullable();
export const partyEducationSchema = yup
    .string()
    .oneOf([null, ...PartyEducation.getAllValues()], 'required')
    .nullable();
export const partyEmploymentSchema = yup
    .string()
    .oneOf([null, ...PartyEmployment.getAllValues()], 'required')
    .nullable();
export const partyCountrySchema = yup
    .string()
    .oneOf([null, ...PartyCountryIsoCode.getAllValues()])
    .nullable()
    .required('required');
export const partyCountryAustriaSchema = yup
    .string()
    .oneOf([CountryIsoCodeEnum.AT], 'austria')
    .nullable()
    .required('required');
export const partyCountryNonRequiredSchema = yup
    .string()
    .oneOf([null, ...PartyCountryIsoCode.getAllValues()])
    .nullable();
export const partyPostalCodeSchema = yup
    .string()
    .nullable()
    .required('required');
export const isValidForeignPostalCode = (postalCode, context) => {
    const { country } = context.parent;
    switch (country) {
        case 'DE':
            return /^[0-9]{5}$|6490a|6490b|6439|6400|1111/.test(postalCode || '');
        // Should be disabled for now: https://azuredevops.at.zurich.com/Zurich/DiTRo/_workitems/edit/35175/
        // case 'SK':
        //   return /^[089]{1}[0-9]{2}\s[0-9]{2}$/.test(postalCode || '');
        // case 'CZ':
        //   return /^[1-7]{1}[0-9]{2}\s[0-9]{2}$/.test(postalCode || '');
        case 'CH':
        case 'LI':
            return /^[1-9][0-9]{3}$/.test(postalCode || '');
        case 'SI':
            return /^[1-9][0-9]{3}$/.test(postalCode || '');
        case 'HU':
            return /^[1-9][0-9]{3}$/.test(postalCode || '');
        case 'IT':
            return /^[0-9]{5}$/.test(postalCode || '');
        default:
            // in case we have no validation for a country
            // the validation will always be true
            return true;
    }
};
export const partyForeignPostalCodeSchema = yup.object().shape({
    country: yup.string(),
    postalCode: yup
        .string()
        .test('validate foreign postalcode', 'required', isValidForeignPostalCode)
        .required('required')
        .nullable(),
});
export const partyForeignPostalCodeNonRequiredSchema = yup.object().shape({
    country: yup.string().nullable(),
    postalCode: yup
        .string()
        .test('validate non-required foreign postalcode', 'invalid', (postalCode, context) => {
        if (!postalCode) {
            return true;
        }
        return isValidForeignPostalCode(postalCode, context);
    })
        .nullable(),
});
export const partyCitySchema = yup.string().nullable().required('required');
export const partyCityNonRequiredSchema = yup.string().nullable();
export const partyStreetSchema = yup
    .string()
    .nullable()
    .max(50, 'isValidStreetName')
    .test('isStreetName', 'required', isStreetName)
    .required('required');
export const partyStreetNonRequiredSchema = yup
    .string()
    .nullable()
    .max(30, '2')
    .test('isStreetName', '1', isStreetName);
export const partyAddressNumberLiveSchema = yup.string().nullable().max(10);
export const partyHouseNumberFromSchema = yup
    .string()
    .nullable()
    .test('isNoSpecialCharacters', '1', isNoSpecialCharacters)
    .max(25, '2')
    .required('required');
export const partyHouseNumberFromNonRequiredSchema = yup
    .string()
    .nullable()
    .test('isNoSpecialCharacters', '1', isNoSpecialCharacters)
    .max(10, '2');
export const partyCustomerRequestMessageSchema = yup.string();
export const partyInquiryMessage = yup.string().required('required');
export const partyHouseNumberToSchema = yup
    .string()
    .nullable()
    .test('isNoSpecialCharacters', '1', isNoSpecialCharacters)
    .max(10, '2');
export const partyStaircaseSchema = yup
    .string()
    .nullable()
    .test('isStairwell', '1', isStairwell);
export const partyDoorNumberSchema = yup
    .string()
    .nullable()
    .test('isDoorNr', '1', isDoorNr);
export const partyPoboxNumberSchema = yup
    .string()
    .nullable()
    .required('required');
export const partyDeliveryNoticeSchema = yup.string().nullable().max(100, '1');
export const partyDeliveryNoticeLiveSchema = yup
    .string()
    .nullable()
    .max(100)
    .test('isMessage', '1', isMessage);
export const partyEmailSchema = yup
    .string()
    .nullable()
    .test('isEmail', '1', isEmail)
    .required('2');
export const eCommunicationSchema = yup
    .string()
    .nullable()
    .required('required');
export const partyEmailSchemaNotRequired = yup
    .string()
    .nullable()
    .test('isEmail', '1', isEmail);
export const partyCommunicationRoleSchema = yup
    .string()
    .nullable()
    .oneOf([...PartyCommunicationRole.getAllValues()], 'required')
    .required('required');
export const partyInsolvencyStartSchema = yup
    .date()
    .transform(parseISODate)
    // .test('<= today', '1', value => (value ? value < startOfTomorrow() : true)) //TODO: make this an universal schema
    .nullable();
export const partyInsolvencyEndSchema = yup.object().shape({
    start: yup.date().transform(parseISODate).nullable(),
    end: yup
        .date()
        .transform(parseISODate)
        //TODO: make this an universal schema
        .test('not before start', '1', (end, context) => {
        if (context.parent.start && end) {
            return context.parent.start < end;
        }
        return true;
    })
        .nullable(),
});
// export const partyInsolvencyModificationReasonSchema = yup
//   .string()
//   .nullable()
export const partyDiplomaticStatusSchema = yup
    .string()
    .nullable()
    .required('required');
export const partyDDocNumberLiveSchema = yup
    .string()
    .matches(/^\d+$/, { excludeEmptyString: true })
    .max(10);
export const partyModificationReasonSchema = yup
    .string()
    .nullable()
    .oneOf(partymodificationReasoneUtils.getValues(), 'required')
    .required('required');
export const partyDDocNumberBaseSchema = yup
    .string()
    .nullable()
    .min(10, 'required')
    .required('required');
export const partyDDocNumberSchema = yup.object({
    dDocNumber: partyDDocNumberBaseSchema
        .nullable()
        .min(9, 'required')
        .max(10, 'required')
        .required('required')
        .test('check color', '1', (value, context) => {
        const { diplomaticStatus } = context.parent;
        if (diplomaticStatus && value) {
            return (value.substring(4, 6) === String(diplomaticStatus) ||
                value.substring(4, 5) === String(diplomaticStatus));
        }
        return true;
    })
        .test('check year', '1', (value, context) => {
        const { dDocSince } = context.parent;
        if (dDocSince && value) {
            return value.substring(0, 4) === dDocSince.substring(0, 4);
        }
        return true;
    }),
    diplomaticStatus: partyDiplomaticStatusSchema,
});
export const partyDDocSinceSchema = yup
    .date()
    .transform(parseISODate)
    .typeError('required')
    .max(startOfTomorrow(), '1')
    .required('required');
export const partyDDocUntilSchema = yup
    .date()
    .transform(parseISODate)
    .typeError('required')
    .required('required');
export const partyCountryCallingCodeSchema = yup
    .string()
    .nullable()
    .oneOf([...PartyCountryCallingCode.getAllValues()], '1');
export const partyAreaCodeSchema = yup
    .string()
    .nullable()
    .test('isProviderCode', '1', isProviderCode)
    .required('required');
export const partyLocalPhoneNumberSchema = yup
    .string()
    .nullable()
    .test('isTelNr', '1', isTelNr)
    .required('required');
export const partyBankAccountNameSchema = yup
    .string()
    .test('isNoSpecialCharacters', '1', partyIsNoSpecialCharacters)
    .nullable();
export const partyBankAccountHolderSchema = yup
    .string()
    .required('required')
    .test('isNoSpecialCharacters', '1', partyIsNoSpecialCharacters)
    .nullable();
export const partyBankAccountBicSchema = yup
    .string()
    .test('isNoSpecialCharacters', '2', isNoSpecialCharacters)
    .test('hasBICLength', '2', (value) => {
    if (value) {
        return value.length === 8 || value.length === 11;
    }
    return true;
})
    .nullable();
export const partyBankAccountBicSchemaRequired = partyBankAccountBicSchema.required('1');
export const partyBankAccountIbanSchema = yup
    .string()
    .required('required')
    .test('isIBAN', '1', isIBAN)
    .test('isZurichIban', '2', (value) => {
    if (value === 'AT426000000001080751' || value === 'AT681200010412006400') {
        return false;
    }
    return true;
})
    .nullable();
export const partyRelationTypeSchema = yup
    .object({
    id: yup.number(),
})
    .required('required')
    .nullable();
export const partyRelationSharesSchema = yup
    .string()
    .required('required')
    .nullable();
export const partyRelationPartySchema = yup
    .object({
    partyNo: yup.string(),
})
    .required('required')
    .nullable();
export const partyRelationVoucherSchema = yup
    .string()
    .oneOf(partyRelationVoucherTypeUtils.getValues(), 'required')
    .nullable()
    .required('required');
export const partyLocalHouseHoldMoveTypeSchema = yup
    .mixed()
    .oneOf(['me', 'all'], '1')
    .required('required');
export const partyLocalHouseHoldMergeSelectionSchema = yup
    .string()
    .nullable()
    .required('required');
export const partyDocumentMetadataDocSubTypeSchema = yup
    .string()
    .nullable()
    .oneOf(partyDocumentDocSubTypeUtils.getValues(), 'required')
    .required('required');
export const partyDocumentNumberSchema = yup
    .string()
    .nullable()
    .min(7, 'required')
    .required('required');
export const partyDocumentNumberLiveSchema = yup
    .string()
    .matches(/^[a-zA-Z0-9\-\\/]+$/, { excludeEmptyString: true })
    .max(32);
export const partyDocumentMetadataDriversLicenseNumberSchema = partyDocumentNumberSchema;
export const partyDocumentMetadataIdentityDocumentNumberSchema = partyDocumentNumberSchema;
export const partyDocumentfromDateSchema = yup.object({
    fromDate: yup
        .date()
        .typeError('required')
        .transform(parseISODate)
        .test('< tomorrow', '2', (fromDate) => {
        if (fromDate) {
            return isPast(fromDate);
        }
        return true;
    })
        .test('after dateOfBirth', '1', (fromDate, context) => {
        if (context.parent.dateOfBirth && fromDate) {
            const dateOfBirth = new Date(context.parent.dateOfBirth);
            const today = new Date();
            // cannot be in the future and needs to be after fromDate
            return (compareAsc(fromDate, today) < 0 &&
                compareAsc(dateOfBirth, fromDate) < 0);
        }
        return true;
    })
        .test('before toDate', '3', (fromDate, context) => {
        if (context.parent.toDate && fromDate) {
            const toDate = new Date(context.parent.toDate);
            const today = new Date();
            // cannot be in the future and needs to be after fromDate
            return (compareAsc(fromDate, today) < 0 && compareDesc(toDate, fromDate) < 0);
        }
        return true;
    })
        .required('required'),
});
export const partyDocumenttoDateSchema = yup.object({
    toDate: yup
        .date()
        .typeError('required')
        .transform(parseISODate)
        .test('before fromDate', '1', (toDate, context) => {
        if (context.parent.fromDate && toDate) {
            const fromDate = new Date(context.parent.fromDate);
            return compareAsc(fromDate, toDate) < 0;
        }
        return true;
    }),
});
export const partyDocumenttoDateRequiredSchema = yup.object({
    toDate: yup
        .date()
        .typeError('required')
        .transform(parseISODate)
        .test('before fromDate', '1', (toDate, context) => {
        if (context.parent.fromDate && toDate) {
            const fromDate = new Date(context.parent.fromDate);
            return compareAsc(fromDate, toDate) < 0;
        }
        return true;
    })
        .required('required'),
});
export const partyDocumentIssueDateSchema = partyDocumentfromDateSchema;
export const partyDocumentExpiryDateSchema = partyDocumenttoDateSchema;
export const partyDocumentExpiryDateRequiredSchema = partyDocumenttoDateRequiredSchema;
export const partyDocumentdDocSinceSchema = partyDocumentfromDateSchema;
export const partyDocumentdDocUntilSchema = partyDocumenttoDateRequiredSchema;
export const partyDocumentIssuingAuthoritySchema = yup
    .string()
    .nullable()
    .required('required');
export const partyNotesNoteRequiredSchema = yup
    .string()
    .nullable()
    .required('required');
